/**
 * InsightsTab Component
 * Main tab component for AI-powered client insights
 *
 * Pattern: Follows NotesTab.tsx and DocumentsTab.tsx structure
 * Features:
 * - Fetches insights on mount
 * - Refresh functionality with 30-second cooldown
 * - Loading skeleton
 * - Empty state
 * - Error state with retry
 * - Source expansion management
 * - Responsive design
 */

import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, Lightbulb } from 'lucide-react';
import { clientService } from '../../../services/clientService';
import { toast } from 'sonner';
import type { ClientInsights } from '../../../types/insights';
import ClientInsightsSummaryCard from './ClientInsightsSummaryCard';
import BehavioralInsightsCard from './BehavioralInsightsCard';
import InsightsSourceCard from './InsightsSourceCard';

interface ClientInsightsTabProps {
  clientId: string;
}

const ClientInsightsTab: React.FC<ClientInsightsTabProps> = ({ clientId }) => {
  // State
  const [insights, setInsights] = useState<ClientInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedSources, setExpandedSources] = useState<Set<string>>(
    new Set(['emails', 'meetings']) // Expand first two sources by default
  );

  // Refresh cooldown management (30 seconds)
  const [canRefresh, setCanRefresh] = useState(true);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const REFRESH_COOLDOWN_MS = 30000; // 30 seconds

  // Fetch insights on mount and when clientId changes
  useEffect(() => {
    fetchInsights();

    // Cleanup timeout on unmount
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [clientId]);

  /**
   * Fetch insights from API (or mock)
   */
  const fetchInsights = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await clientService.getInsights(clientId);
      setInsights(data);
    } catch (err: any) {
      console.error('Error fetching insights:', err);

      // Handle 404 gracefully (backend not ready)
      if (err?.response?.status === 404 || err?.status === 404) {
        setError('Insights endpoint not yet available');
      } else {
        setError('Failed to load insights');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle refresh button click
   * Includes 30-second cooldown to prevent hammering backend
   */
  const handleRefresh = async () => {
    if (!canRefresh) {
      toast.info('Please wait before refreshing again');
      return;
    }

    setRefreshing(true);
    setCanRefresh(false);

    try {
      // Trigger regeneration (simulated for now)
      await clientService.regenerateInsights(clientId);

      // Fetch updated insights
      await fetchInsights();

      toast.success('Insights updated successfully');

      // Start cooldown timer
      refreshTimeoutRef.current = setTimeout(() => {
        setCanRefresh(true);
      }, REFRESH_COOLDOWN_MS);
    } catch (err) {
      console.error('Error refreshing insights:', err);
      toast.error('Failed to refresh insights');
      setCanRefresh(true); // Allow retry on error
    } finally {
      setRefreshing(false);
    }
  };

  /**
   * Toggle source expansion
   */
  const toggleSourceExpansion = (sourceType: string) => {
    const newExpanded = new Set(expandedSources);
    if (newExpanded.has(sourceType)) {
      newExpanded.delete(sourceType);
    } else {
      newExpanded.add(sourceType);
    }
    setExpandedSources(newExpanded);
  };

  // Loading state - skeleton
  if (loading) {
    return (
      <div className="space-y-4">
        {/* Summary skeleton */}
        <div className="border border-gray-200 rounded-lg p-6 animate-pulse">
          <div className="flex items-center justify-between mb-4">
            <div className="h-5 bg-gray-200 rounded w-40" />
            <div className="h-5 bg-gray-200 rounded w-24" />
          </div>
          <div className="h-4 bg-gray-200 rounded w-full mb-2" />
          <div className="h-4 bg-gray-200 rounded w-5/6 mb-2" />
          <div className="h-4 bg-gray-200 rounded w-4/6" />
        </div>

        {/* Source cards skeleton */}
        {[...Array(4)].map((_, i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-4 animate-pulse">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-200 rounded" />
                <div className="h-4 bg-gray-200 rounded w-32" />
              </div>
              <div className="h-8 bg-gray-200 rounded w-20" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Error state
  if (error || !insights) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <p className="text-gray-600 font-medium mb-4">
          {error || 'No insights available'}
        </p>
        <button
          onClick={fetchInsights}
          className="px-4 py-2 bg-kielis-navy text-white rounded-md hover:bg-opacity-90 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // Main content
  return (
    <div className="space-y-6">
      {/* AI Summary Card */}
      <ClientInsightsSummaryCard
        summary={insights.ai_summary}
        lastUpdated={insights.last_updated}
        confidenceScore={insights.confidence_score}
        onRefresh={handleRefresh}
        isRefreshing={refreshing}
      />

      {/* Behavioral Insights */}
      {insights.behavioral_insights && (
        <BehavioralInsightsCard insights={insights.behavioral_insights} />
      )}

      {/* Source Cards Section */}
      <div className="space-y-3">
        <InsightsSourceCard
          title="Recent Emails"
          icon="📧"
          source={insights.sources.emails}
          sourceType="emails"
          isExpanded={expandedSources.has('emails')}
          onToggleExpand={() => toggleSourceExpansion('emails')}
        />

        <InsightsSourceCard
          title="Meeting Notes"
          icon="📝"
          source={insights.sources.meetings}
          sourceType="meetings"
          isExpanded={expandedSources.has('meetings')}
          onToggleExpand={() => toggleSourceExpansion('meetings')}
        />

        <InsightsSourceCard
          title="Portfolio Updates"
          icon="📊"
          source={insights.sources.portfolio}
          sourceType="portfolio"
          isExpanded={expandedSources.has('portfolio')}
          onToggleExpand={() => toggleSourceExpansion('portfolio')}
        />

        <InsightsSourceCard
          title="Market News"
          icon="📰"
          source={insights.sources.market_news}
          sourceType="market_news"
          isExpanded={expandedSources.has('market_news')}
          onToggleExpand={() => toggleSourceExpansion('market_news')}
        />
      </div>

      {/* AI Recommendations */}
      {insights.recommendations && insights.recommendations.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="w-5 h-5 text-amber-600" aria-hidden="true" />
            <h3 className="text-lg font-semibold text-amber-900">Recommendations</h3>
          </div>
          <ul className="space-y-2">
            {insights.recommendations.map((recommendation, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5 flex-shrink-0" aria-hidden="true">
                  •
                </span>
                <p className="text-sm text-gray-700">{recommendation}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Cooldown notice (only shown when refreshing is disabled) */}
      {!canRefresh && (
        <div className="text-xs text-gray-500 text-center">
          Refresh available in {Math.ceil(REFRESH_COOLDOWN_MS / 1000)} seconds
        </div>
      )}
    </div>
  );
};

export default ClientInsightsTab;
